/**
 * Adobe Analytics (Siemens OTE) utility.
 *
 * Behaviour:
 *  - The ote_config.js / ote.js scripts are loaded as static tags in index.html
 *    (end of <body>) per Siemens cookbook requirements. This utility provides
 *    the React-side integration layer on top of those scripts.
 *
 *  - trackAdobePageView() sends a virtual page view for SPA route changes.
 *    The Siemens OTE AppMeasurement object is exposed on window.s — we call
 *    s.t() after updating s.pageName to reflect the current route.
 *
 *  - Adobe Launch (which loads UserCentrics consent + Cookie Monster) is
 *    injected dynamically via VITE_ADOBE_LAUNCH_SRC when that env var is set.
 *    Until the Launch script is received from Christian Rottwinkel, this env
 *    var should remain empty — the rest of the integration still works.
 *
 *  - registerConsentCallback() is a stub ready to be wired up to the
 *    Cookie Monster API once the Launch script is in place.
 */

const LAUNCH_SRC = import.meta.env.VITE_ADOBE_LAUNCH_SRC;

let launchLoaded = false;

/**
 * Returns true when the OTE AppMeasurement object is available on the page.
 * The s object is set by ote.js after it loads.
 */
export function isOteReady() {
  return typeof window !== 'undefined' && typeof window.s === 'object' && window.s !== null;
}

/**
 * Returns true when Adobe Launch src is configured.
 */
export function isLaunchEnabled() {
  return Boolean(LAUNCH_SRC);
}

/**
 * Inject the Adobe Launch script.
 * Called once on app mount. No-op if VITE_ADOBE_LAUNCH_SRC is not set.
 * Adobe Launch will automatically load the UserCentrics Consent Management
 * Layer and Cookie Monster.
 */
export function initAdobeLaunch() {
  if (!isLaunchEnabled() || launchLoaded) return;
  launchLoaded = true;

  const script = document.createElement('script');
  script.id = 'adobe-launch-script';
  script.async = true;
  script.src = LAUNCH_SRC;
  document.head.appendChild(script);
}

/**
 * Register callbacks on the Cookie Monster consent API.
 *
 * TODO (Phase 2 — unblock once Adobe Launch + Cookie Monster scripts are received):
 *  - Replace stub with real Cookie Monster callback registration.
 *  - Confirm category names for "Performance / Statistics" cookies with
 *    Christian Rottwinkel / adlytics.
 *  - See Cookie Monster documentation linked in the Siemens cookbook (slide 12).
 *
 * Example shape (subject to Cookie Monster API docs):
 *   window.cookieMonster.onConsentGiven('performance', () => {
 *     // enable Adobe Analytics tracking
 *   });
 *   window.cookieMonster.onConsentRevoked('performance', () => {
 *     // disable Adobe Analytics tracking
 *   });
 */
export function registerConsentCallback() {
  // Stub — implement after Adobe Launch + Cookie Monster are available.
}

/**
 * Send a virtual page view to Adobe Analytics for SPA route changes.
 * Safe to call before ote.js has fully evaluated — the call is silently
 * skipped if window.s is not yet available.
 *
 * The OTE AppMeasurement object (window.s) is set by ote.js.
 * We update s.pageName and call s.t() to fire the page view beacon.
 *
 * NOTE: If adlytics requires a different tracking call (e.g. a digitalData
 * push or _satellite.track), update the body of this function accordingly
 * after confirming with the analytics team.
 *
 * @param {string} path - e.g. "/dm" or "/admin"
 */
export function trackAdobePageView(path) {
  if (!isOteReady()) return;

  // Build a human-readable page name from the path for Adobe Analytics reports.
  // Siemens convention: "siemens.com:learning-day:<section>"
  const section = path === '/' ? 'home' : path.replace(/^\//, '').replace(/\//g, ':');
  const pageName = `learning-day.sws.siemens.com:${section}`;

  window.s.pageName = pageName;
  window.s.t();
}
